import React, { useEffect, useState } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useAppBarContext } from "../components/AppBarContext";
import { db } from "../firebase";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  CardMedia,
  useMediaQuery,
  ButtonBase,
} from "@mui/material";
import PublicListings from './PublicListings'; // Import the PublicListings component
import { useAuth } from "../auth";
import Loading from "../components/Loading";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import { styled } from "@mui/material/styles";
import { inkColorMap, rarityIcons } from "./constants";

const currencySymbols = {
  AUD: '$',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  // Add more currencies as needed
};

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));

const PublicCardDetail = ({ setId, id }) => {
  const [card, setCard] = useState(null);
  const [listings, setListings] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [sortBy, setSortBy] = useState("price"); // State for sorting criteria
  const [filterCondition, setFilterCondition] = useState(""); // State for filtering condition
  const { selectedCountry, exchangeRates } = useAppBarContext();
  const { currentUser } = useAuth();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [expanded, setExpanded] = React.useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        const cardDoc = await getDoc(doc(db, "sets", setId, "cards", id));
        setCard({ id: cardDoc.id, ...cardDoc.data() });
      } catch (error) {
        console.error("Error fetching card details:", error);
      }
    };

    const fetchListings = async () => {
      try {
        const listingsSnapshot = await getDocs(
          collection(db, "sets", setId, "cards", id, "listings")
        );
        const listingsData = listingsSnapshot.docs.flatMap((doc) =>
          Object.entries(doc.data()).map(([condition, data]) => ({
            ...data,
            docName: doc.id,
            condition,
          }))
        );

        // Fetch usernames for the listings
        const userIds = [
          ...new Set(listingsData.map((listing) => listing.userId)),
        ];
        const usernamesData = {};
        for (const userId of userIds) {
          const userDoc = await getDoc(doc(db, "users", userId));
          if (userDoc.exists()) {
            usernamesData[userId] = userDoc.data().username;
          }
        }
        setUsernames(usernamesData);
        setListings(listingsData);
      } catch (error) {
        console.error("Error fetching listings:", error);
      }
    };

    fetchCardDetails();
    fetchListings();
  }, [id, setId]);

  const convertPrice = (usdPrice) => {
    if (!exchangeRates[selectedCountry]) return usdPrice;
    return `${currencySymbols[selectedCountry]}${(
      usdPrice * exchangeRates[selectedCountry]
    ).toFixed(2)}`;
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterCondition(event.target.value);
  };

  const addToCart = async (listing, price, setId) => {
    alert('Sign in required to add to cart');
  };

  if (!card) return <Loading />;

  const DetailRow = ({ label, value }) => (
    value ? <Typography variant="body1"><strong>{label}:</strong> {value}</Typography> : null
  );

  return (
    <Box sx={{ padding: 1 }}>
      <Card
        sx={{
          width: "100%",
          border: `4px solid ${inkColorMap[card.ink] || "#F2D18B"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* CardHeader */}
          <CardHeader
            avatar={
              <Box sx={{ display: "flex", flexDirection: "column"}}>
                <Avatar variant="square" sx={{ bgcolor: "#0C0A5A", mb: 1 }}>
                  {card.collector_number}
                </Avatar>
                <Avatar variant="square" sx={{ bgcolor: "#0C0A5A" }}>
                  <img
                    src={rarityIcons[card.rarity]}
                    alt={card.rarity}
                    style={{ width: 24, height: 24 }}
                  />
                </Avatar>
              </Box>
            }
            title={
              <Typography variant="h6" sx={{ fontSize: "1.5rem" }}>
                {card.name}
              </Typography>
            }
            subheader={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                  {card.version}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "1rem", color: "gray", fontStyle: "italic" }}
                >
                  {card.set.name}
                </Typography>
              </Box>
            }
            sx={{ paddingBottom: 2 }}
          />

          {/* CardMedia */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CardMedia
              component="img"
              alt={card.name}
              image={
                card.image_uris?.digital?.normal ||
                card.image_uris?.digital?.large
              }
              title={card.name}
              sx={{
                // Responsive size control
                height: {
                  xs: "80%", // Large height on mobile devices
                  sm: "50%", // Medium height on tablet devices
                  md: "30%", // Smaller height on desktop devices
                },
                width: {
                  xs: "80%", // Large width on mobile devices
                  sm: "50%", // Medium width on tablet devices
                  md: "30%", // Smaller width on desktop devices
                },
                objectFit: "contain",
                margin: "auto",
                borderRadius: 2
              }}
            />
          </Box>
        </Box>

        <CardActions
          disableSpacing
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ButtonBase
              href={`https://www.tcgplayer.com/product/${card.tcgplayer_id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "90%",
                padding: 1,
                border: "1px solid",
                textAlign: "left",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                TCG Player Price:
              </Typography>
              <Typography variant="body1" sx={{ marginLeft: 1 }}>
                {convertPrice(card.price || 0)}
              </Typography>
            </ButtonBase>
          </Box>

          <ButtonBase
            onClick={handleExpandClick} // This will make the entire section clickable
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "30%",
              padding: 1,
              border: "1px solid",
              textAlign: "left",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "gray" }}>
              Card Info
            </Typography>
            <ExpandMore
              expand={expanded}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </ButtonBase>
        </CardActions>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <DetailRow label="Version" value={card.version} />
            <DetailRow label="Detail" value={card.text} />
            <DetailRow label="Rarity" value={card.rarity} />
            <DetailRow label="Ink Type" value={card.ink} />
            <DetailRow label="Cost" value={card.cost} />
            <DetailRow
              label="Type"
              value={
                Array.isArray(card.type) ? card.type.join(", ") : card.type
              }
            />
            <DetailRow
              label="Classifications"
              value={card.classifications?.join(", ")}
            />
            <DetailRow label="Flavor Text" value={card.flavor_text} />
            <DetailRow
              label="Illustrators"
              value={card.illustrators?.join(", ")}
            />
            <DetailRow label="Strength" value={card.strength} />
            <DetailRow label="Willpower" value={card.willpower} />
          </CardContent>
        </Collapse>
      </Card>
          <PublicListings
            cardId={id}
            setId={setId}
            listings={listings}
            usernames={usernames} // Pass usernames to PublicListings component
            sortBy={sortBy}
            filterCondition={filterCondition}
            convertPrice={convertPrice}
            addToCart={(listing, price) => addToCart(listing, price, setId)} // Override addToCart function
            handleSortChange={handleSortChange}
            handleFilterChange={handleFilterChange}
            card={card} // Pass card data to PublicListings component
          />
    </Box>
  );
};

export default PublicCardDetail;
